import { encryptedSize } from "@limeshare/lsc1";

export const GiB = 1024 ** 3;
export const MiB = 1024 ** 2;
export const HOUR = 3600;
export const DAY = 86_400;

export type Tier = "guest" | "free" | "pro" | "business";

export interface TierLimits {
  tier: Tier;
  /** files per transfer */
  maxFiles: number;
  /** plaintext bytes per file */
  maxFileBytes: number;
  /** plaintext bytes per transfer */
  maxTransferBytes: number;
  /** seconds; null = while subscription active (managed by billing) */
  retentionSeconds: number | null;
  /** plaintext bytes per rolling day per subject (ip/fingerprint/account) */
  dailyIngestBytes: number;
  /** transfer initiations per hour per subject */
  initsPerHour: number;
  /** bytes; null = n/a */
  activeStorageBytes: number | null;
  passwordAllowed: boolean;
  downloadLimitAllowed: boolean;
  perFileLinks: boolean;
  /** anonymous uploads must pass Turnstile */
  turnstileRequired: boolean;
}

export const TIERS: Record<Tier, TierLimits> = {
  guest: {
    tier: "guest",
    maxFiles: 500,
    maxFileBytes: 5 * GiB,
    maxTransferBytes: 5 * GiB,
    retentionSeconds: 30 * DAY,
    dailyIngestBytes: 100 * GiB,
    initsPerHour: 50,
    activeStorageBytes: null,
    passwordAllowed: true,
    downloadLimitAllowed: false,
    perFileLinks: true,
    turnstileRequired: true,
  },
  free: {
    tier: "free",
    maxFiles: 500,
    maxFileBytes: 5 * GiB,
    maxTransferBytes: 25 * GiB,
    retentionSeconds: 30 * DAY,
    dailyIngestBytes: 100 * GiB,
    initsPerHour: 50,
    activeStorageBytes: 250 * GiB,
    passwordAllowed: true,
    downloadLimitAllowed: true,
    perFileLinks: true,
    turnstileRequired: false,
  },
  pro: {
    tier: "pro",
    maxFiles: 2500,
    maxFileBytes: 500 * GiB,
    maxTransferBytes: 500 * GiB,
    retentionSeconds: 365 * DAY,
    dailyIngestBytes: 2 * 1024 * GiB,
    initsPerHour: 200,
    activeStorageBytes: 1024 * GiB,
    passwordAllowed: true,
    downloadLimitAllowed: true,
    perFileLinks: true,
    turnstileRequired: false,
  },
  business: {
    tier: "business",
    maxFiles: 2500,
    maxFileBytes: 500 * GiB,
    maxTransferBytes: 1024 * GiB,
    retentionSeconds: 365 * DAY,
    dailyIngestBytes: 5 * 1024 * GiB,
    initsPerHour: 500,
    activeStorageBytes: 2 * 1024 * GiB,
    passwordAllowed: true,
    downloadLimitAllowed: true,
    perFileLinks: true,
    turnstileRequired: false,
  },
};

/**
 * Smart Gravity Retention:
 * - Up to 100 MB -> 30 days free
 * - 100 MB to 1 GB -> 7 days free
 * - 1 GB to 5 GB -> 72 hours (3 days) free
 */
export function smartRetentionSeconds(totalPlainBytes: number): number {
  if (totalPlainBytes <= 100 * MiB) return 30 * DAY;
  if (totalPlainBytes <= 1 * GiB) return 7 * DAY;
  return 72 * HOUR;
}

/** Legacy Boost SKUs kept for backwards compatibility */
export interface BoostSku {
  sku: "boost-100g-7d" | "boost-500g-14d";
  priceUsd: number;
  maxFiles: number;
  maxFileBytes: number;
  maxTransferBytes: number;
  retentionSeconds: number;
}

export const BOOSTS: Record<BoostSku["sku"], BoostSku> = {
  "boost-100g-7d": {
    sku: "boost-100g-7d",
    priceUsd: 2.99,
    maxFiles: 2500,
    maxFileBytes: 100 * GiB,
    maxTransferBytes: 100 * GiB,
    retentionSeconds: 7 * DAY,
  },
  "boost-500g-14d": {
    sku: "boost-500g-14d",
    priceUsd: 6.99,
    maxFiles: 2500,
    maxFileBytes: 500 * GiB,
    maxTransferBytes: 500 * GiB,
    retentionSeconds: 14 * DAY,
  },
};

/** Full catalog of NOWPayments SKUs (Exceed, Extend, Rescue, Vault Pass, Pro, Teams) */
export type ProductSkuId =
  | "exceed-50g"
  | "exceed-200g"
  | "exceed-1tb"
  | "extend-6m"
  | "extend-1y"
  | "rescue-1y"
  | "vault-pass-15"
  | "pro-annual"
  | "teams-25"
  | "paywall-unlock";

export interface ProductSku {
  sku: ProductSkuId;
  kind: "exceed" | "extend" | "rescue" | "pass" | "pro" | "teams";
  name: string;
  badge?: string;
  priceUsd: number;
  description: string;
  maxFiles: number;
  maxTransferBytes: number;
  retentionSeconds: number;
  creditsIncluded?: number;
  seats?: number;
}

export const PRODUCTS: Record<ProductSkuId, ProductSku> = {
  "exceed-50g": {
    sku: "exceed-50g",
    kind: "exceed",
    name: "LimeShare Exceed 50 GB",
    priceUsd: 2.49,
    description: "Send up to 50 GB in a single transfer · 7 days storage · No signup",
    maxFiles: 2500,
    maxTransferBytes: 50 * GiB,
    retentionSeconds: 7 * DAY,
  },
  "exceed-200g": {
    sku: "exceed-200g",
    kind: "exceed",
    name: "LimeShare Exceed 200 GB",
    badge: "Most Popular",
    priceUsd: 4.49,
    description: "Send up to 200 GB in a single transfer · 14 days storage · No signup",
    maxFiles: 2500,
    maxTransferBytes: 200 * GiB,
    retentionSeconds: 14 * DAY,
  },
  "exceed-1tb": {
    sku: "exceed-1tb",
    kind: "exceed",
    name: "LimeShare Exceed 1 TB",
    priceUsd: 8.99,
    description: "Send up to 1 TB in a single transfer · 14 days storage · No signup",
    maxFiles: 2500,
    maxTransferBytes: 1024 * GiB,
    retentionSeconds: 14 * DAY,
  },
  "extend-6m": {
    sku: "extend-6m",
    kind: "extend",
    name: "KeepAlive Extend — 6 Months",
    priceUsd: 1.99,
    description: "Keep this encrypted transfer online for 180 days · Unlimited downloads · No signup",
    maxFiles: 500,
    maxTransferBytes: 5 * GiB,
    retentionSeconds: 180 * DAY,
  },
  "extend-1y": {
    sku: "extend-1y",
    kind: "extend",
    name: "KeepAlive Extend — 1 Year",
    badge: "Best Value",
    priceUsd: 2.99,
    description: "Keep this encrypted transfer online for 365 days · Unlimited downloads · No signup",
    maxFiles: 500,
    maxTransferBytes: 5 * GiB,
    retentionSeconds: 365 * DAY,
  },
  "rescue-1y": {
    sku: "rescue-1y",
    kind: "rescue",
    name: "Recipient Rescue — Freeze Timer (1 Year)",
    priceUsd: 1.99,
    description: "Freeze the expiration timer and keep these files available for 365 days",
    maxFiles: 500,
    maxTransferBytes: 5 * GiB,
    retentionSeconds: 365 * DAY,
  },
  "vault-pass-15": {
    sku: "vault-pass-15",
    kind: "pass",
    name: "Anonymous Vault Pass (10 Credits)",
    badge: "+25% Bonus",
    priceUsd: 15.0,
    description: "16-digit zero-KYC pass code good for 10 any-size Boosts (up to 200 GB) or 1-Year Extends",
    maxFiles: 2500,
    maxTransferBytes: 200 * GiB,
    retentionSeconds: 365 * DAY,
    creditsIncluded: 10,
  },
  "pro-annual": {
    sku: "pro-annual",
    kind: "pro",
    name: "LimeShare Extra 1000 (1 TB Pro)",
    badge: "Creators Choice",
    priceUsd: 49.0,
    description: "1 TB encrypted storage · Up to 500 GB per transfer · 365-day retention · Unlimited transfers",
    maxFiles: 2500,
    maxTransferBytes: 500 * GiB,
    retentionSeconds: 365 * DAY,
    creditsIncluded: 250,
  },
  "teams-25": {
    sku: "teams-25",
    kind: "teams",
    name: "LimeShare Teams — 25 People, One Price",
    badge: "Zero Per-Seat Fees",
    priceUsd: 149.0,
    description: "25 team members · 2 TB shared encrypted pool · Dual Personal/Team mode · Certified Destruction",
    maxFiles: 2500,
    maxTransferBytes: 1024 * GiB,
    retentionSeconds: 365 * DAY,
    creditsIncluded: 1000,
    seats: 25,
  },
  "paywall-unlock": {
    sku: "paywall-unlock",
    kind: "rescue",
    name: "Pay-to-Decrypt File Unlock",
    priceUsd: 15.0,
    description: "Unlock full master download for this paywalled creator delivery",
    maxFiles: 500,
    maxTransferBytes: 50 * GiB,
    retentionSeconds: 30 * DAY,
  },
};

/* ------------------------------------------------------------------ */
/* Container / upload planning */

/** Default container chunk: 4 MiB (chunkLog2 = 22). */
export const CHUNK_LOG2 = 22;

/** Largest possible container for a given plaintext limit (used for server-side validation). */
export function maxEncryptedSizeFor(plaintextLimit: number): number {
  return encryptedSize(plaintextLimit, CHUNK_LOG2);
}

/**
 * Inverse of encryptedSize for the default chunk size. Returns the plaintext length L such that
 * encryptedSize(L) === E, or null if E is not a valid LSC1 container length. The solution is unique.
 * Lets the server validate declared sizes and enforce plaintext-based tier limits without seeing plaintext.
 */
export function plaintextSizeFromEncrypted(E: number, chunkLog2: number = CHUNK_LOG2): number | null {
  if (!Number.isSafeInteger(E) || E < 48) return null;
  const C = 2 ** chunkLog2;
  const body = E - 32;
  const n0 = Math.floor(body / (C + 16));
  for (const n of new Set([1, n0 - 1, n0, n0 + 1])) {
    if (n < 1) continue;
    const L = body - 16 * n;
    if (L < 0) continue;
    if (Math.max(1, Math.ceil(L / C)) === n) return L;
  }
  return null;
}

export const PART_SIZE_SMALL = 8 * MiB;
export const PART_SIZE_MEDIUM = 16 * MiB;
export const PART_SIZE_LARGE = 64 * MiB;
export const MAX_PARTS = 10_000;
export const PART_URL_BATCH = 50;
export const PART_URL_TTL_SECONDS = 2 * HOUR;
export const DOWNLOAD_URL_TTL_SECONDS = 30 * 60;

/** §8 — 8 MiB ≤ 1 GiB, 16 MiB ≤ 10 GiB, else 64 MiB. All parts of a file are equal except the last (R2 rule). */
export function planPartSize(encryptedBytes: number): number {
  if (encryptedBytes <= 1 * GiB) return PART_SIZE_SMALL;
  if (encryptedBytes <= 10 * GiB) return PART_SIZE_MEDIUM;
  return PART_SIZE_LARGE;
}

export function partCountFor(encryptedBytes: number, partSize: number): number {
  return Math.max(1, Math.ceil(encryptedBytes / partSize));
}

/** Draft lifetime: enough for a slow (1 MiB/s) upload ×2, clamped to [3h, 24h]. */
export function draftTtlSeconds(totalEncryptedBytes: number): number {
  const needed = Math.ceil((totalEncryptedBytes / MiB) * 2);
  return Math.min(24 * HOUR, Math.max(3 * HOUR, needed));
}

/** Burn-on-read: how long objects survive after the first successful access (§9 step 5). */
export const BURN_GRACE_SECONDS = 30 * 60;

/* ------------------------------------------------------------------ */
/* Object storage layout — prefix determines the lifecycle-rule backstop */

export type StoragePrefix = "g" | "f" | "p" | "b";

export function storagePrefixFor(tier: Tier, boosted: boolean): StoragePrefix {
  if (boosted) return "b";
  switch (tier) {
    case "guest":
      return "g";
    case "free":
      return "f";
    case "pro":
    case "business":
      return "p";
  }
}

export function objectKey(prefix: StoragePrefix, transferId: string, fileId: string): string {
  return `${prefix}/${transferId}/${fileId}`;
}
